import { Alert } from '@mui/material';
import { RefreshCwIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type {
  ContractDetail,
  ContractTable,
  FaceValue,
  RowInput,
} from '@/lib/api/credit-contracts';
import { addMonths, formatPeriod, monthLabel, type Period } from '@/lib/credit-contracts/month';
import { periodOf, type FieldErrors, type FieldKey } from '@/lib/credit-contracts/validation';
import { formatNumber, parseNumberInput } from '@/lib/credit-conversion-settings/number';

import { creditWithYen, numberText, planLabels } from './display';
import { MonthField } from './MonthPicker';
import type { ContractRow, Dialog as DialogState, EditDialog } from './useContractDetail';

const tableLabels: Record<ContractTable, string> = {
  'pricing-plans': '契約料金プラン',
  'data-connect-credits': 'データコネクトのクレジット数',
};

export interface RowDialogProps {
  dialog: DialogState | null;
  detail: ContractDetail;
  busy: boolean;
  onChange: (key: FieldKey, value: string) => void;
  onConfirm: () => void;
  onBack: () => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
  onRefetch: () => void;
}

/** 契約詳細の、行の追加・編集（入力と保存の前の確認）と削除の確認 */
export function RowDialog({
  dialog,
  detail,
  busy,
  onChange,
  onConfirm,
  onBack,
  onSave,
  onDelete,
  onClose,
  onRefetch,
}: RowDialogProps) {
  if (!dialog) return null;
  const company = `${detail.company.name}（${detail.company.id}）`;
  const label = tableLabels[dialog.table];
  const failure = dialog.failure;
  const verb = dialog.kind === 'delete' ? '削除' : '保存';
  const title =
    dialog.kind === 'delete'
      ? `${label}の削除の確認`
      : dialog.step === 'confirm'
        ? `${label}の保存の確認`
        : `${label}の${dialog.original ? '編集' : '追加'}`;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{company}</DialogDescription>
        </DialogHeader>

        {dialog.kind === 'edit' && dialog.step === 'input' && (
          <RowForm
            dialog={dialog}
            current={detail.current_month}
            disabled={busy}
            onChange={onChange}
          />
        )}
        {dialog.kind === 'edit' && dialog.step === 'confirm' && (
          <Confirmation
            heading={dialog.original ? '次の内容で保存します。' : '次の行を追加します。'}
            table={dialog.table}
            input={dialog.input}
            faces={detail.credit_face_values}
            adjust={dialog.adjust}
            addedStart={periodOf(dialog.input).start_month}
          />
        )}
        {dialog.kind === 'delete' && (
          <Confirmation
            heading="次の行を削除します。"
            table={dialog.table}
            row={dialog.original}
            faces={detail.credit_face_values}
          />
        )}

        {dialog.kind === 'edit' && dialog.step === 'confirm' && (
          <ConfirmErrors errors={dialog.errors} generalErrors={dialog.generalErrors} />
        )}
        {failure && <Alert severity="error">{failure.message}</Alert>}
        {dialog.frozen && failure?.kind !== 'unknown' && (
          <Alert severity="warning">
            {`${verb}できたかが分からない後のため、入力は変えられません。同じ内容のまま、もう一度${verb}するか、入力内容を破棄して契約の値を取得し直してください。`}
          </Alert>
        )}

        <DialogFooter>
          {dialog.frozen || (failure && failure.kind !== 'retry') ? (
            // 保存・削除し直せないとき・反映できたかが分からない後は、取得し直すか、もう一度同じ内容で行うだけ
            <Button variant="outline" onClick={onRefetch} disabled={busy}>
              <RefreshCwIcon />
              {dialog.frozen ? '破棄して取得し直す' : '取得し直す'}
            </Button>
          ) : (
            <>
              {dialog.kind === 'edit' && dialog.step === 'confirm' && (
                <Button variant="outline" onClick={onBack} disabled={busy}>
                  戻る
                </Button>
              )}
              <Button variant="outline" onClick={onClose} disabled={busy}>
                キャンセル
              </Button>
            </>
          )}
          {dialog.kind === 'edit' && dialog.step === 'input' ? (
            <Button onClick={onConfirm} disabled={busy}>
              確認へ
            </Button>
          ) : (
            (dialog.frozen || failure?.kind !== 'refetch') && (
              <Button
                variant={dialog.kind === 'delete' ? 'destructive' : 'default'}
                onClick={dialog.kind === 'delete' ? onDelete : onSave}
                disabled={busy}
              >
                {dialog.frozen ? `もう一度${verb}する` : `${verb}する`}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const fieldLabels: Record<FieldKey, string> = {
  start_month: '開始月',
  end_month: '終了月',
  pricing_plan: '料金プラン',
  monthly_contract_credit: '契約クレジット数',
  credit_unit_price: 'クレジット販売単価',
  monthly_fee: '月額料金',
  monthly_credit: 'クレジット数',
};

/** 確認の段で返ったサーバーのバリデーションのエラー（反映できたかが分からない後は入力に戻さないため、ここに示す） */
function ConfirmErrors({
  errors,
  generalErrors,
}: {
  errors: FieldErrors;
  generalErrors: string[];
}) {
  const messages = [
    ...(Object.entries(errors) as [FieldKey, string][]).map(
      ([key, message]) => `${fieldLabels[key]}：${message}`,
    ),
    ...generalErrors,
  ];
  if (messages.length === 0) return null;
  return (
    <Alert severity="error" data-error="true">
      {messages.map((message) => (
        <div key={message}>{message}</div>
      ))}
    </Alert>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-red-600" data-error="true">
      {message}
    </p>
  );
}

/** 入力欄1つ（ラベル・入力・補足・エラー） */
function Field({
  name,
  label,
  unit,
  value,
  errors,
  disabled,
  hint,
  inputMode,
  labelClassName,
  onChange,
}: {
  name: FieldKey;
  label: string;
  /** 単位。見出しが途中で折り返さないよう、2段目に示す */
  unit?: string;
  value: string;
  errors: FieldErrors;
  disabled: boolean;
  hint?: string;
  inputMode?: 'decimal' | 'numeric';
  /** 横に並べた欄の見出しの行数がそろわないとき、高さをそろえる */
  labelClassName?: string;
  onChange: (key: FieldKey, value: string) => void;
}) {
  const id = `contract-${name}`;
  const error = errors[name];
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className={labelClassName}>
        {unit ? (
          <span className="flex flex-col gap-1">
            <span>{label}</span>
            <span>{unit}</span>
          </span>
        ) : (
          label
        )}
      </Label>
      <Input
        id={id}
        value={value}
        inputMode={inputMode}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={error ? 'border-red-500' : undefined}
        onChange={(e) => onChange(name, e.target.value)}
      />
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/** 追加・編集の入力 */
function RowForm({
  dialog,
  current,
  disabled,
  onChange,
}: {
  dialog: EditDialog;
  /** サーバーが求めた当月（`YYYY-MM`） */
  current: string;
  disabled: boolean;
  onChange: (key: FieldKey, value: string) => void;
}) {
  const { input, errors, mode } = dialog;
  // 反映できたかが分からない旨を示した後は、入力を変えられない
  const frozen = disabled || dialog.frozen;
  const active = mode.table === 'data-connect-credits' && mode.kind === 'active';
  // 月を選ぶ小窓で選べない月。データコネクトのクレジット数の追加と予定の行は当月より後に始まり、
  // 適用中の行の終了月は当月以後とする（手で打った値は「入力値の規則」のとおりにチェックする）
  const startsAfterCurrent = mode.table === 'data-connect-credits' && !active;
  const common = { errors, onChange };
  return (
    <div className="flex flex-col gap-4">
      {dialog.generalErrors.length > 0 && (
        <Alert severity="error" data-error="true">
          {dialog.generalErrors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </Alert>
      )}
      {active && (
        <p className="text-sm text-gray-600">
          適用中の行は、終了月だけを変えられます。終了月は、空欄か、当月以後の月にしてください。
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <MonthField
          id="contract-start_month"
          label="開始月"
          value={input.start_month}
          onChange={(v) => onChange('start_month', v)}
          current={current}
          min={startsAfterCurrent ? addMonths(current, 1) : undefined}
          disabled={frozen || active}
          error={errors.start_month}
          // データコネクトの開始月は当月より後に限るので、その旨と、選べる最初の月を例に示す
          hint={
            startsAfterCurrent
              ? `当月より後の月（例：${monthLabel(addMonths(current, 1))}）`
              : 'YYYY/MM（例：2026/11）'
          }
        />
        <MonthField
          id="contract-end_month"
          label="終了月"
          value={input.end_month}
          onChange={(v) => onChange('end_month', v)}
          current={current}
          min={active ? current : undefined}
          clearable
          disabled={frozen}
          error={errors.end_month}
          hint="空欄なら終了月なし（継続中）"
        />
      </div>
      {mode.table === 'pricing-plans' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label id="contract-pricing_plan-label">料金プラン</Label>
            <RadioGroup
              aria-labelledby="contract-pricing_plan-label"
              value={input.pricing_plan ?? ''}
              onValueChange={(v) => onChange('pricing_plan', v)}
              disabled={frozen}
              className="flex gap-6"
            >
              {(['Credit', 'FixedFee'] as const).map((plan) => (
                <div key={plan} className="flex items-center gap-2">
                  <RadioGroupItem value={plan} id={`contract-plan-${plan}`} />
                  <Label htmlFor={`contract-plan-${plan}`}>{planLabels[plan]}</Label>
                </div>
              ))}
            </RadioGroup>
            <FieldError id="contract-pricing_plan-error" message={errors.pricing_plan} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field
              {...common}
              name="monthly_contract_credit"
              labelClassName="flex min-h-10 items-end"
              label="契約クレジット数"
              unit="（クレジット/月）"
              value={input.monthly_contract_credit ?? ''}
              disabled={frozen || input.pricing_plan !== 'Credit'}
              inputMode="decimal"
            />
            <Field
              {...common}
              name="credit_unit_price"
              labelClassName="flex min-h-10 items-end"
              label="クレジット販売単価"
              unit="（円/クレジット）"
              value={input.credit_unit_price ?? ''}
              disabled={frozen || input.pricing_plan !== 'Credit'}
              inputMode="numeric"
            />
            <Field
              {...common}
              name="monthly_fee"
              labelClassName="flex min-h-10 items-end"
              label="月額料金"
              unit="（円）"
              value={input.monthly_fee ?? ''}
              disabled={frozen || input.pricing_plan !== 'FixedFee'}
              inputMode="numeric"
            />
          </div>
        </>
      ) : (
        <Field
          {...common}
          name="monthly_credit"
          label="クレジット数（クレジット/月）"
          value={input.monthly_credit ?? ''}
          disabled={frozen || active}
          inputMode="decimal"
        />
      )}
    </div>
  );
}

/** 入力値の表示。数値は、入力の形式どおりなら区切って示す */
function inputNumber(value: string | undefined): string {
  const parsed = parseNumberInput(value ?? '');
  return parsed.ok ? formatNumber(parsed.canonical) : (value ?? '');
}

/** 保存・削除する行の内容。追加で終了月を合わせる行があれば、その行と合わせた後の終了月も示す */
function Confirmation({
  heading,
  table,
  input,
  row,
  faces,
  adjust,
  addedStart,
}: {
  heading: string;
  table: ContractTable;
  input?: RowInput;
  row?: ContractRow;
  faces: FaceValue[];
  adjust?: ContractRow | null;
  addedStart?: string;
}) {
  const period: Period = input
    ? periodOf(input)
    : { start_month: row!.start_month, end_month: row!.end_month };
  const items: [string, string][] = [['期間', formatPeriod(period)]];
  if (table === 'pricing-plans') {
    const plan = input?.pricing_plan || (row && 'pricing_plan' in row ? row.pricing_plan : '');
    const value = (key: 'monthly_contract_credit' | 'credit_unit_price' | 'monthly_fee') =>
      input ? inputNumber(input[key]) : numberText(row && 'pricing_plan' in row ? row[key] : null);
    items.push(['料金プラン', plan ? planLabels[plan] : '']);
    if (plan === 'Credit') {
      items.push(['契約クレジット数（クレジット/月）', value('monthly_contract_credit')]);
      items.push(['クレジット販売単価（円/クレジット）', value('credit_unit_price')]);
    } else {
      items.push(['月額料金（円）', value('monthly_fee')]);
    }
  } else {
    const credit =
      input?.monthly_credit ?? (row && 'monthly_credit' in row ? row.monthly_credit : '');
    items.push(['クレジット数（クレジット/月）', creditWithYen(credit, period.start_month, faces)]);
  }
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p>{heading}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border bg-gray-50 px-4 py-3">
        {items.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-gray-500">{k}</dt>
            <dd className="font-medium tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
      {adjust && addedStart && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          あわせて、期間が重なる行（{formatPeriod(adjust)}）の終了月を、
          <strong>{monthLabel(addMonths(addedStart, -1))}</strong>にします。
        </p>
      )}
    </div>
  );
}
